/**
 * Therapist Setup Dialog + associated widgets — split out of
 * pages/Payroll.jsx (2026-02). Manages the therapist roster: bulk
 * xlsx import, duplicate banner, individual edit/create dialogs, and
 * the small Field/SelectF form primitives used by TherapistForm.
 *
 * Zero behaviour change from the pre-split inline definitions.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Plus, Users, Upload, Download, RefreshCw, AlertTriangle, Shield, CheckCircle2 } from 'lucide-react';
import {
  C, sortTherapistsForSetup,
} from '../../lib/payrollHelpers';

// ═══════════════ Therapist Setup Dialog ═══════════════
function TherapistSetupDialog({ onClose }) {
  const [rows, setRows] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [importResult, setImportResult] = useState(null); // {created, updated, skipped[]}
  const [importing, setImporting] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/payroll/therapists').then(({ data }) => {
      setRows(sortTherapistsForSetup(data.therapists || []));
      setDuplicates(data.duplicates || []);
    })
      .catch(() => toast.error('Could not load therapist roster'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const save = async (payload, id) => {
    try {
      if (id) await api.put(`/payroll/therapists/${id}`, payload);
      else await api.post('/payroll/therapists', payload);
      toast.success('Saved');
      setEditing(null); setCreating(false); load();
    } catch { toast.error('Save failed'); }
  };

  const remove = async (t) => {
    const label = t?.attendance_name || 'this therapist';
    if (!window.confirm(`Remove "${label}" from the roster?\n\nThe row is soft-deleted — it drops out of future payroll runs and duplicate checks, but historical archived runs still show them.`)) return;
    try {
      await api.delete(`/payroll/therapists/${t.therapist_id}`);
      toast.success(`Removed ${label}`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Delete failed';
      toast.error(msg);
    }
  };

  const downloadTemplate = async () => {
    try {
      // XLSX with dropdowns on every enum column + current roster prefilled.
      const r = await api.get('/payroll/therapists/template.xlsx', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'thrive-therapist-setup.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download template');
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/payroll/therapists/bulk-import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(data);
      const totalOk = (data.created || 0) + (data.updated || 0);
      const skipped = (data.skipped || []).length;
      if (totalOk === 0 && skipped === 0) {
        toast.error('No rows imported');
      } else {
        toast.success(`Imported: ${data.created || 0} added, ${data.updated || 0} updated${skipped ? `, ${skipped} skipped` : ''}`);
      }
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Bulk import failed';
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="pr-setup-dialog">
        <DialogHeader>
          <DialogTitle>Therapist Setup</DialogTitle>
          <DialogDescription>
            Attendance name = SP Client Attendance export · Sessions name = SP Appointment Status export.
            Only <b>Active + Unit-Based</b> therapists appear in the payroll calculator.
          </DialogDescription>
        </DialogHeader>
        {duplicates.length > 0 && (
          <DuplicateRosterBanner
            duplicates={duplicates}
            onEdit={(t) => setEditing(t)}
            rows={rows}
          />
        )}
        <div className="flex flex-wrap gap-2 justify-end mb-2">
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="pr-setup-download-template">
            <Download size={13} className="mr-1" /> Download template
          </Button>
          <label className={`inline-flex items-center h-8 px-3 rounded-md border text-[13px] cursor-pointer transition-colors ${importing ? 'opacity-60 pointer-events-none' : 'hover:bg-[#F1EDF5]'}`}
                 style={{ borderColor: C.border, color: C.ink }}
                 data-testid="pr-setup-bulk-import-label">
            <Upload size={13} className="mr-1" />
            {importing ? 'Importing…' : 'Bulk import'}
            <input
              type="file"
              accept=".csv,.xlsx"
              className="hidden"
              disabled={importing}
              onChange={e => {
                const f = e.target.files?.[0];
                e.target.value = '';   // allow re-selecting the same file
                if (f) handleUpload(f);
              }}
              data-testid="pr-setup-bulk-import-input"
            />
          </label>
          <Button size="sm" onClick={() => setCreating(true)} data-testid="pr-setup-add">
            <Plus size={13} className="mr-1" /> Add therapist
          </Button>
        </div>
        {importResult && (
          <BulkImportResult result={importResult} onDismiss={() => setImportResult(null)} />
        )}
        {loading ? <div className="p-4 text-sm text-slate-500">Loading…</div> : (
          <table className="w-full text-[13px]">
            <thead className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>
              <tr>
                <th className="text-left py-2">Attendance name</th>
                <th className="text-left py-2">Sessions name</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Category</th>
                <th className="text-right py-2">Therapy $</th>
                <th className="text-right py-2">Eval $ (92523)</th>
                <th className="text-right py-2">LC/NS $</th>
                <th className="text-right py-2">Bonus ≥</th>
                <th className="text-right py-2">Bonus $</th>
                <th className="text-center py-2">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map(t => (
                <tr key={t.therapist_id} className="border-t" style={{ borderColor: C.border }} data-testid={`pr-setup-row-${t.therapist_id}`}>
                  <td className="py-2">{t.attendance_name}</td>
                  <td className="py-2 text-[12px]" style={{ color: t.sessions_name !== t.attendance_name ? C.gold : C.soft }}>{t.sessions_name}</td>
                  <td className="py-2 text-[12px]">
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      t.therapist_type === 'SLP' ? 'bg-[#EFE9F5] text-[#4A3B5F]'
                      : t.therapist_type === 'SLPA' ? 'bg-[#E4EEE9] text-[#3B6F5D]'
                      : 'bg-slate-100 text-slate-500'
                    }`}>{t.therapist_type || 'Other'}</span>
                  </td>
                  <td className="py-2 text-[12px]">{t.category}</td>
                  <td className="py-2 text-right tabular-nums" style={{ color: t.therapist_type === 'SLP' ? C.ink : C.soft, opacity: t.therapist_type === 'SLP' ? 1 : 0.4 }}>
                    {t.therapist_type === 'SLP' ? `$${t.therapy_session_rate || 0}` : '—'}
                  </td>
                  <td className="py-2 text-right tabular-nums" style={{ color: t.therapist_type === 'SLP' ? C.ink : C.soft, opacity: t.therapist_type === 'SLP' ? 1 : 0.4 }}>
                    {t.therapist_type === 'SLP' ? `$${t.evaluation_rate || 0}` : '—'}
                  </td>
                  <td className="py-2 text-right tabular-nums">${t.lc_ns_rate}</td>
                  <td className="py-2 text-right tabular-nums">{t.bonus_threshold}</td>
                  <td className="py-2 text-right tabular-nums">${t.bonus_amount}</td>
                  <td className="py-2 text-center">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.status === 'Active' ? 'bg-[#E4EEE9] text-[#3B6F5D]' : 'bg-slate-100 text-slate-500'}`}>{t.status}</span>
                  </td>
                  <td className="py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => setEditing(t)} data-testid={`pr-setup-edit-${t.therapist_id}`}>Edit</Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => remove(t)}
                        className="text-[#B54B4B] border-[#F2D1D1] hover:bg-[#FDECEA]"
                        data-testid={`pr-setup-delete-${t.therapist_id}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
        {(editing || creating) && (
          <TherapistForm
            therapist={editing}
            onCancel={() => { setEditing(null); setCreating(false); }}
            onSubmit={(payload) => save(payload, editing?.therapist_id)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}



function DuplicateRosterBanner({ duplicates, rows, onEdit }) {
  const primary = duplicates.filter(d => d.kind === 'primary_collision');
  const aliasShadow = duplicates.filter(d => d.kind === 'alias_shadows_primary');
  const rowById = useMemo(() => {
    const m = new Map();
    (rows || []).forEach(r => m.set(r.therapist_id, r));
    return m;
  }, [rows]);
  return (
    <Card className="mb-3 p-3 border-l-4" style={{
      borderColor: C.rose, borderLeftColor: C.rose, background: '#FDECEA',
    }} data-testid="pr-setup-duplicate-banner">
      <div className="flex items-start gap-2 text-[12px]" style={{ color: C.rose }}>
        <AlertTriangle size={14} style={{ marginTop: 2 }} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[13px]">
            {primary.length + aliasShadow.length} duplicate roster row{primary.length + aliasShadow.length === 1 ? '' : 's'} detected — silent last-writer-wins can drop payroll credit.
          </div>
          <div className="mt-0.5 text-[11px]" style={{ color: C.rose, opacity: 0.9 }}>
            Two Active + Unit-Based rows can't share the same name. Fix by inactivating one row, renaming, or merging aliases.
          </div>

          {primary.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-widest font-bold">Primary name collisions</div>
              <ul className="mt-1 space-y-1">
                {primary.map((d, i) => (
                  <li key={i} className="text-[12px]" data-testid={`pr-setup-dup-primary-${i}`}>
                    <span className="font-semibold">&ldquo;{d.rows[0].attendance_name}&rdquo;</span>
                    {' '}(&times;{d.count}) &mdash;{' '}
                    {d.rows.map((r, j) => {
                      const full = rowById.get(r.therapist_id);
                      return (
                        <React.Fragment key={r.therapist_id}>
                          <button
                            type="button"
                            onClick={() => full && onEdit(full)}
                            className="underline decoration-dotted hover:decoration-solid"
                            data-testid={`pr-setup-dup-edit-${r.therapist_id}`}
                          >
                            {r.therapist_id.slice(0, 10)}&hellip;{r.role_title ? ` (${r.role_title})` : ''}
                          </button>
                          {j < d.rows.length - 1 && <span>, </span>}
                        </React.Fragment>
                      );
                    })}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {aliasShadow.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-widest font-bold">Alias shadows a primary name</div>
              <ul className="mt-1 space-y-1">
                {aliasShadow.map((d, i) => {
                  const shadower = rowById.get(d.shadowed_by?.therapist_id);
                  return (
                    <li key={i} className="text-[12px]" data-testid={`pr-setup-dup-alias-${i}`}>
                      <span className="font-semibold">&ldquo;{d.alias}&rdquo;</span> on{' '}
                      <button
                        type="button"
                        onClick={() => shadower && onEdit(shadower)}
                        className="underline decoration-dotted hover:decoration-solid"
                        data-testid={`pr-setup-dup-alias-edit-${d.shadowed_by?.therapist_id}`}
                      >
                        {d.shadowed_by?.attendance_name}
                      </button>
                      {' '}collides with primary{' '}
                      {(d.would_shadow || []).map(w => w.attendance_name).join(', ')} &mdash;
                      the primary wins in compute, but the alias is misleading; remove or rename it.
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}


function BulkImportResult({ result, onDismiss }) {
  const created = result?.created || 0;
  const updated = result?.updated || 0;
  const skipped = result?.skipped || [];
  const hasIssues = skipped.length > 0;
  return (
    <Card className="mb-3 p-3 border-l-4" style={{
      borderColor: hasIssues ? C.amber : C.ok,
      borderLeftColor: hasIssues ? C.amber : C.ok,
      background: hasIssues ? '#FBF5E9' : '#EEF7F2',
    }} data-testid="pr-setup-bulk-result">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 text-[12px]" style={{ color: hasIssues ? '#8A5D2D' : C.ok }}>
          {hasIssues ? <AlertTriangle size={13} style={{ marginTop: 2 }} /> : <CheckCircle2 size={13} style={{ marginTop: 2 }} />}
          <div>
            <div className="font-semibold">
              Imported {created + updated} therapist{created + updated === 1 ? '' : 's'} — {created} added, {updated} updated{hasIssues ? `, ${skipped.length} skipped` : ''}.
            </div>
            {hasIssues && (
              <ul className="mt-1 space-y-0.5">
                {skipped.slice(0, 8).map((s, i) => (
                  <li key={i} className="text-[11px]" data-testid={`pr-setup-bulk-skipped-${i}`}>
                    Row {s.row}{s.attendance_name ? ` (${s.attendance_name})` : ''}: {s.reason}
                  </li>
                ))}
                {skipped.length > 8 && (
                  <li className="text-[11px]">… and {skipped.length - 8} more</li>
                )}
              </ul>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-[11px] px-2 py-0.5 rounded-full border"
          style={{ borderColor: hasIssues ? '#B08A3A' : C.ok, color: hasIssues ? '#8A5D2D' : C.ok }}
          data-testid="pr-setup-bulk-result-dismiss"
        >
          Dismiss
        </button>
      </div>
    </Card>
  );
}


function TherapistForm({ therapist, onCancel, onSubmit }) {
  const [f, setF] = useState(therapist ? { ...therapist, aliases: therapist.aliases || [] } : {
    attendance_name: '', sessions_name: '', aliases: [], role_title: '',
    pay_type: 'Unit-Based', category: 'Employee',
    therapist_type: 'Other',
    therapy_session_rate: 0, evaluation_rate: 0,
    lc_ns_rate: 25, bonus_threshold: 110, bonus_amount: 100, status: 'Active',
    start_date: '',
  });
  // Aliases live in the form as an array but the input is a comma-separated
  // string — HR pastes a list from the "unmatched" HR warning strip and we
  // parse it out on save.
  const [aliasesStr, setAliasesStr] = useState((therapist?.aliases || []).join(', '));
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));
  const isSlp = f.therapist_type === 'SLP';
  // Student Placement clinicians have zero comp — the entire pay block is
  // hidden so HR can't accidentally set a rate. Backend also force-zeros
  // on save (iter 218).
  const isStudent = f.category === 'Student Placement';
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent data-testid="pr-therapist-form">
        <DialogHeader>
          <DialogTitle>{therapist ? 'Edit therapist' : 'Add therapist'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Attendance name (SP)" v={f.attendance_name} on={v => upd('attendance_name', v)} tid="tform-att-name" />
          <Field label="Sessions name (SP)" v={f.sessions_name} on={v => upd('sessions_name', v)} tid="tform-sess-name" />
          <Field label="Role / title" v={f.role_title} on={v => upd('role_title', v)} tid="tform-role" />
          <SelectF label="Pay type" v={f.pay_type} options={['Unit-Based', 'Salaried']} on={v => upd('pay_type', v)} tid="tform-paytype" />
          <SelectF label="Category" v={f.category} options={['Employee', 'Contractor-Flat', 'Contractor-CPT', 'Student Placement']} on={v => upd('category', v)} tid="tform-category" />
          <SelectF label="Therapist type" v={f.therapist_type} options={['Other', 'SLP', 'SLPA']} on={v => upd('therapist_type', v)} tid="tform-ttype" />
          <SelectF label="Status" v={f.status} options={['Active', 'Inactive']} on={v => upd('status', v)} tid="tform-status" />
          <Field label="Start date" v={f.start_date || ''} on={v => upd('start_date', v)} type="date" tid="tform-start-date" />
          {!isStudent && <Field label="LC/NS rate ($)" v={f.lc_ns_rate} on={v => upd('lc_ns_rate', v)} type="number" tid="tform-lcns" />}
          {!isStudent && <Field label="Bonus threshold" v={f.bonus_threshold} on={v => upd('bonus_threshold', v)} type="number" tid="tform-bthr" />}
          {!isStudent && <Field label="Bonus amount ($)" v={f.bonus_amount} on={v => upd('bonus_amount', v)} type="number" tid="tform-bamt" />}
        </div>
        {/* Aliases — extra SP-name variants (maiden names, initials-only, historical exports). */}
        <div className="mt-3">
          <label className="block">
            <div className="text-[11px] flex items-center justify-between" style={{ color: C.soft }}>
              <span>Also known as (comma-separated aliases)</span>
              {(f.aliases?.length || 0) > 0 && (
                <span className="text-[10px]" data-testid="tform-aliases-count">{f.aliases.length} alias{f.aliases.length === 1 ? '' : 'es'}</span>
              )}
            </div>
            <Input
              type="text"
              value={aliasesStr}
              onChange={e => {
                const s = e.target.value;
                setAliasesStr(s);
                const parsed = s.split(',').map(x => x.trim()).filter(Boolean);
                upd('aliases', parsed);
              }}
              placeholder="e.g. Kate Robertson, K. Carriere, Katherine Carriere"
              className="mt-1"
              data-testid="tform-aliases"
            />
            <div className="text-[11px] mt-1" style={{ color: C.soft }}>
              Any SP name in this list will be attributed to this therapist during payroll — use it to clear the &ldquo;unmatched&rdquo; warnings without editing the SP exports.
              <span className="block mt-0.5">
                Note: credentials (M.S., CCC-SLP, CF-SLP, OTR/L, PhD, etc.) and middle initials are auto-stripped &mdash; you don&rsquo;t need an alias for those.
              </span>
            </div>
          </label>
        </div>
        {/* SLP-only rate block — hidden entirely for Student Placement */}
        {!isStudent && (
        <div className={`mt-2 rounded-xl border p-3 ${isSlp ? '' : 'opacity-60'}`}
             style={{ borderColor: C.border, background: isSlp ? '#F6F2FA' : '#F8F8F8' }}
             data-testid="tform-slp-rates">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: isSlp ? C.plum : C.soft }}>
            SLP per-CPT rates {!isSlp && '(applies to SLPs only)'}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Therapy Session rate ($ per unit, non-92523)" v={f.therapy_session_rate}
                   on={v => upd('therapy_session_rate', v)} type="number" tid="tform-therapy-rate" />
            <Field label="Evaluation rate ($ per 92523 unit)" v={f.evaluation_rate}
                   on={v => upd('evaluation_rate', v)} type="number" tid="tform-eval-rate" />
          </div>
          <div className="text-[11px] mt-2" style={{ color: C.soft }}>
            SLPAs don't do 92523 — leave 0 or select Therapist type = SLPA.
          </div>
        </div>
        )}
        {isStudent && (
          <div className="mt-2 rounded-xl border p-3 text-[12px]"
               style={{ borderColor: C.border, background: '#FCFBFD', color: C.soft }}
               data-testid="tform-student-note">
            <b>Student Placement</b> — no comp is applicable. Their attendance and session counts still appear on the payroll run for reporting, but they contribute $0 to payroll cost totals.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button className="bg-[#4A3B5F] hover:bg-[#2D2833] text-white"
                  onClick={() => onSubmit({
                    ...f,
                    aliases: (f.aliases || []).map(a => String(a).trim()).filter(Boolean),
                    // Student Placement: force every comp field to 0 regardless of what's in state.
                    lc_ns_rate: isStudent ? 0 : Number(f.lc_ns_rate),
                    bonus_threshold: isStudent ? 0 : Number(f.bonus_threshold),
                    bonus_amount: isStudent ? 0 : Number(f.bonus_amount),
                    therapy_session_rate: isStudent ? 0 : Number(f.therapy_session_rate || 0),
                    evaluation_rate: isStudent ? 0 : Number(f.evaluation_rate || 0),
                    start_date: (f.start_date || '').slice(0, 10),
                  })}
                  data-testid="tform-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function Field({ label, v, on, type = 'text', tid }) {
  return (
    <label className="block">
      <div className="text-[11px]" style={{ color: C.soft }}>{label}</div>
      <Input type={type} value={v ?? ''} onChange={e => on(e.target.value)} className="mt-1" data-testid={tid} />
    </label>
  );
}
function SelectF({ label, v, options, on, tid }) {
  return (
    <label className="block">
      <div className="text-[11px]" style={{ color: C.soft }}>{label}</div>
      <select value={v} onChange={e => on(e.target.value)} className="mt-1 w-full border rounded h-9 px-2 text-sm bg-white" style={{ borderColor: C.border }} data-testid={tid}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}


export { TherapistSetupDialog, TherapistForm };
